import { type FC, useState, useEffect, Suspense } from 'react';
import {
    type PreloadedQuery,
    graphql,
    useQueryLoader,
    usePreloadedQuery,
    useRelayEnvironment,
} from 'react-relay';

import SV from 'server-vars';
import {
    trackEvent,
    eventNameConstants,
    stepInteractionConstants,
    interactionTypeConstants,
} from 'dibs-tracking';
import { handleLocaleUrl } from 'dibs-intl/exports/urls';
import { type Locale } from 'dibs-intl/exports/locales';
import { Button } from 'dibs-elements/exports/Button';
import { ModalContainer } from 'dibs-elements/exports/ModalContainer';
import { ModalBody } from 'dibs-elements/exports/ModalBody';
import { ModalSpinner } from 'dibs-elements/exports/ModalSpinner';
import { BarHeader } from 'dibs-elements/exports/BarHeader';
import { ModalCloseButton } from 'dibs-elements/exports/ModalCloseButton';
import { Toast } from 'dibs-toaster/exports/Toast';
import Logo from 'dibs-logo/exports/Logo';
import { trackAbTestV2Variant, getAbTestV2 } from 'dibs-ab-tests/exports/clientAbTestV2';

import { setLangCookie } from '../mutations/setLangCookieMutation';

import { type LanguageDiscrepancyModalQuery as LanguageDiscrepancyModalQueryType } from './__generated__/LanguageDiscrepancyModalQuery.graphql';

import styles from './styles.scss';

const { EVENT_PRODUCT_INTERACTION } = eventNameConstants;
const { INTERACTION_TYPE_INTERNATIONAL } = interactionTypeConstants;
const { STEP_INTERACTION_MODAL_DISPLAY, STEP_INTERACTION_MODAL_CLOSE } = stepInteractionConstants;

const LanguageDiscrepancyModalQuery = graphql`
    query LanguageDiscrepancyModalQuery($locale: String!) {
        viewer {
            internationalCopy(locale: $locale) {
                languageDiscrepancyModal {
                    title
                    description
                    yesContinueButton
                    noThanksButton
                }
            }
        }
    }
`;

const sharedTrackingArgs = {
    eventName: EVENT_PRODUCT_INTERACTION,
    interaction_type: INTERACTION_TYPE_INTERNATIONAL,
    trigger: 'intlLanguageDiscrepancyModal',
    category: 'international',
    label: `${SV.get('settings.pageType') || 'unknown page'} - intl`,
};

function trackModal(action = '', step_interaction_name = '', isInteractiveEvent = true): void {
    trackEvent({
        ...sharedTrackingArgs,
        action,
        step_interaction_name,
        isInteractiveEvent,
    });
}

const LanguageDiscrepancyBody: FC<{
    queryReference: PreloadedQuery<LanguageDiscrepancyModalQueryType>;
    onClose: () => void;
    isMobile?: boolean;
    locale: Locale;
}> = ({ queryReference, onClose, isMobile, locale }) => {
    const environment = useRelayEnvironment();
    const data = usePreloadedQuery<LanguageDiscrepancyModalQueryType>(
        LanguageDiscrepancyModalQuery,
        queryReference
    );

    const labels = data?.viewer?.internationalCopy?.languageDiscrepancyModal;
    const buttonSize = isMobile ? 'medium' : 'large';

    return (
        <>
            {!isMobile && (
                <div className={styles.logoContainer}>
                    <Logo className={styles.logo} />
                </div>
            )}
            {isMobile ? (
                <div className={styles.toastHeader}>{labels?.title}</div>
            ) : (
                <BarHeader title={labels?.title} />
            )}
            <div className={styles.languageDiscrepancyDescription}>{labels?.description}</div>
            <div>
                <Button
                    size={buttonSize}
                    fullWidth
                    onClick={async () => {
                        trackModal('intl lang discrepancy modal yes, continue click', 'yes');
                        await setLangCookie(environment, {
                            locale,
                            cookieDomain: SV.get('cookieDomain'),
                        });
                        window.location.assign(handleLocaleUrl(window.location.pathname, locale));
                        onClose();
                    }}
                >
                    {labels?.yesContinueButton}
                </Button>
                <Button
                    size={buttonSize}
                    className={styles.saveAndContinueButton}
                    fullWidth
                    type={isMobile ? 'transparent' : 'secondary'}
                    onClick={() => {
                        trackModal('intl lang discrepancy modal no, thanks click', 'no');
                        onClose();
                    }}
                >
                    {labels?.noThanksButton}
                </Button>
            </div>
        </>
    );
};

export const LanguageDiscrepancyModal: FC<{
    isModalOpen: boolean;
    isMobile: boolean;
    locale: Locale;
    runNextNotification?: () => void;
    onClose?: () => void;
}> = ({ isModalOpen, onClose = () => {}, isMobile, locale, runNextNotification }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [queryReference, loadQuery] = useQueryLoader<LanguageDiscrepancyModalQueryType>(
        LanguageDiscrepancyModalQuery
    );
    const isAbTest = getAbTestV2('notificationQueueCleanupV1')?.variant === 'variant';

    useEffect(() => {
        trackAbTestV2Variant('notificationQueueCleanupV1');
    }, []);

    useEffect(() => {
        if (!isAbTest) {
            setIsOpen(isModalOpen);
            if (isModalOpen) {
                trackModal(
                    'intl lang discrepancy modal display',
                    STEP_INTERACTION_MODAL_DISPLAY,
                    false
                );
                loadQuery({ locale });
            }
        }
    }, [isModalOpen, loadQuery, locale, isAbTest]);

    if (isAbTest) {
        trackModal('intl lang discrepancy modal display', STEP_INTERACTION_MODAL_DISPLAY, false);
        runNextNotification?.();
        return null;
    }

    function close(): void {
        onClose();
        setIsOpen(false);
    }

    function closeAndTrack(): void {
        trackModal('intl lang discrepancy modal exit click', STEP_INTERACTION_MODAL_CLOSE);
        close();
    }

    if (!queryReference) {
        return null;
    }

    const sharedProps = {
        onClose: close,
        isMobile,
        queryReference,
        locale,
    };

    if (isMobile && isOpen) {
        return (
            <Suspense fallback={''}>
                <Toast onClose={closeAndTrack}>
                    <LanguageDiscrepancyBody {...sharedProps} />
                </Toast>
            </Suspense>
        );
    }
    return (
        <ModalContainer isOpen={isOpen} onClose={closeAndTrack}>
            <ModalBody>
                <ModalCloseButton onClick={closeAndTrack} />
                <Suspense fallback={<ModalSpinner isOpen />}>
                    <LanguageDiscrepancyBody {...sharedProps} />
                </Suspense>
            </ModalBody>
        </ModalContainer>
    );
};
